/**
 * @file        src/pages/seo/SeoRobots.jsx
 * @description SEO Management → robots.txt: the brand's custom robots.txt (empty
 *              = serve the generated default), a line diff against that default,
 *              and the sitemap the storefront will publish.
 *
 *              The generated default and the served file both come from the
 *              server (GET /api/seo/robots-preview → the resolver's own
 *              robotsTxt / defaultRobotsTxt), so the diff compares against what
 *              would really be served — including the private paths every
 *              robots.txt disallows and the Sitemap line built from the
 *              canonical host.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import { getRobotsPreview, getSitemapStatus, saveSeoSettings } from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { BrandBar, PickBrand, errMessage, useSeoBrand, withBrand } from './seoShared';

/** Line diff by longest common subsequence: [{op:' '|'+'|'-', text}]. Inputs are a few dozen lines. */
function lineDiff(fromText, toText) {
  const a = String(fromText || '').replace(/\s+$/, '').split('\n');
  const b = String(toText || '').replace(/\s+$/, '').split('\n');
  const n = a.length; const m = b.length;
  const L = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) {
    L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  }
  const out = [];
  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ op: ' ', text: a[i] }); i++; j++; } else if (L[i + 1][j] >= L[i][j + 1]) { out.push({ op: '-', text: a[i] }); i++; } else { out.push({ op: '+', text: b[j] }); j++; }
  }
  while (i < n) out.push({ op: '-', text: a[i++] });
  while (j < m) out.push({ op: '+', text: b[j++] });
  return out;
}

export default function SeoRobots() {
  const qc = useQueryClient();
  const [brand, setBrand] = useSeoBrand();
  const [text, setText] = useState('');
  const [showDefault, setShowDefault] = useState(false);

  const robots = useQuery({
    queryKey: qk.seo.robots(brand),
    queryFn: () => getRobotsPreview(brand),
    enabled: Boolean(brand),
  });
  const sitemap = useQuery({
    queryKey: qk.seo.sitemap(brand),
    queryFn: () => getSitemapStatus(brand),
    enabled: Boolean(brand),
  });
  const r = robots.data?.data;
  const sm = sitemap.data?.data;
  useEffect(() => { if (r) setText(r.custom || ''); }, [r]);

  const saveMut = useMutation({
    mutationFn: (value) => saveSeoSettings({ brand, robots_txt: value }),
    onSuccess: (_res, value) => {
      toast.ok('Saved', value ? 'Custom robots.txt saved.' : 'Now serving the generated default.');
      qc.invalidateQueries({ queryKey: qk.seo.root });
    },
    onError: (err) => toast.err('Could not save', errMessage(err)),
  });

  const usingDefault = !text.trim();
  const diff = r && !usingDefault ? lineDiff(r.generated, text) : null;
  const changed = diff ? diff.filter((d) => d.op !== ' ').length : 0;

  return (
    <>
      <PageHead title="robots.txt" subtitle="SEO Management · what crawlers are allowed to fetch, and the sitemap they are pointed at" />
      <BrandBar brand={brand} onChange={setBrand} />
      {!brand ? <PickBrand /> : robots.isLoading ? (
        <article className="panel glass"><div className="empty">Loading…</div></article>
      ) : robots.isError ? (
        <article className="panel glass"><div className="empty"><p className="field-error">Could not load robots.txt.</p></div></article>
      ) : (
        <>
          {r.blocked ? (
            <div className="notice-banner danger">
              <span>
                <b>“Do not index this site” is ON</b> — the storefront serves <code>Disallow: /</code> regardless of
                anything below. What you save here takes effect only once the kill-switch is off.
              </span>
              <Link to={withBrand('/seo/settings', brand)}>Global SEO →</Link>
            </div>
          ) : null}
          {!r.canonicalHost ? (
            <div className="notice-banner">
              <span>
                <b>Canonical host is not set</b> — the generated file carries no <code>Sitemap:</code> line, and the
                storefront answers <code>Disallow: /</code> on every host until it is set.
              </span>
              <Link to={withBrand('/seo/settings', brand)}>Global SEO →</Link>
            </div>
          ) : null}

          <article className="panel glass">
            <div className="panel-head">
              <div>
                <h3 className="panel-title">Custom robots.txt</h3>
                <p className="muted">
                  Leave empty to serve the generated default (recommended) — it already blocks checkout, booking and
                  account pages and points crawlers at your sitemap.
                </p>
              </div>
              <Chip tone={usingDefault ? 'ok' : 'pend'}>{usingDefault ? 'Serving generated default' : 'Custom file'}</Chip>
            </div>
            <div className="field full">
              <textarea className="seo-code" rows={14} value={text} onChange={(e) => setText(e.target.value)}
                spellCheck={false} maxLength={20000} placeholder={r.generated} />
            </div>
            <div className="form-actions" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn ghost sm" onClick={() => setShowDefault((v) => !v)}>
                  <Icon name="eye" /> {showDefault ? 'Hide' : 'Preview'} generated default
                </button>
                <button type="button" className="btn ghost sm" onClick={() => setText(r.generated)}>
                  Start from the default
                </button>
                {!usingDefault ? (
                  <button type="button" className="btn ghost sm" onClick={() => setText('')}>Clear (use default)</button>
                ) : null}
              </div>
              <button type="button" className="btn primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate(text.trim() ? text : '')}>
                <Icon name="check" /> {saveMut.isPending ? 'Saving…' : 'Save robots.txt'}
              </button>
            </div>

            {showDefault ? (
              <>
                <div className="seo-preview-label" style={{ marginTop: 16 }}>Generated default</div>
                <div className="seo-diff">{r.generated.replace(/\n$/, '').split('\n').map((l, i) => <div key={i}>{l || ' '}</div>)}</div>
              </>
            ) : null}

            {diff ? (
              <>
                <div className="seo-preview-label" style={{ marginTop: 16 }}>
                  Your file vs the generated default — {changed ? `${changed} line(s) differ` : 'identical'}
                </div>
                <div className="seo-diff">
                  {diff.map((d, i) => (
                    <div key={i} className={d.op === '+' ? 'add' : d.op === '-' ? 'del' : undefined}>
                      {d.op} {d.text || ' '}
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            <div className="seo-preview-label" style={{ marginTop: 16 }}>Served right now (saved settings)</div>
            <div className="seo-diff">{r.served.replace(/\n$/, '').split('\n').map((l, i) => <div key={i}>{l || ' '}</div>)}</div>
          </article>

          <article className="panel glass" style={{ marginTop: 16 }}>
            <div className="panel-head">
              <div>
                <h3 className="panel-title">Sitemap</h3>
                <p className="muted">
                  {sm?.sitemapUrl ? <>Published at <code>{sm.sitemapUrl}</code>.</> : 'No sitemap URL until the canonical host is set.'}
                </p>
              </div>
              {sm ? <Chip tone={sm.enabled ? 'ok' : 'rej'}>{sm.enabled ? 'Enabled' : 'Disabled'}</Chip> : null}
            </div>
            {sitemap.isLoading ? <div className="empty">Loading…</div> : sitemap.isError ? (
              <div className="empty"><p className="field-error">Could not load the sitemap status.</p></div>
            ) : sm ? (
              <>
                <div className="mini-kpi">
                  <article className="mk glass neo"><small>URLs</small><b>{sm.urlCount}</b></article>
                  <article className="mk glass neo"><small>Blog posts</small><b>{sm.blogs}</b></article>
                  <article className="mk glass neo"><small>Packages</small><b>{sm.packages}</b></article>
                </div>
                {!sm.enabled ? <p className="muted">The sitemap is switched off in Global SEO, so it lists nothing.</p> : null}
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Page</th><th>URL</th><th>Priority</th><th>Change frequency</th></tr></thead>
                    <tbody>
                      {sm.pages.map((p) => (
                        <tr key={p.pageKey}>
                          <td><Link to={withBrand(`/seo/pages/${encodeURIComponent(p.pageKey)}`, brand)}>{p.label}</Link></td>
                          <td className="mono">{p.loc || p.path}</td>
                          <td className="mono">{p.priority != null ? Number(p.priority).toFixed(1) : '—'}</td>
                          <td>{p.changefreq || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </article>
        </>
      )}
    </>
  );
}
